import {
  getCurrentFiiScreenDescriptor,
  getCurrentFixedIncomeScreenDescriptor,
  type CurrentScreenToolInput,
} from './current-screen-catalog';
import { ativoPath } from '@/libs/ativo-route';

export type ChatScreenAction = {
  label: string;
  /** Validated same-origin app path (never treated as authorization). */
  href: string;
};

export type ChatScreenContext = {
  id: string;
  label: string;
  description: string;
  modelPrompt: string;
  toolInput?: CurrentScreenToolInput;
  /** Ask-style proactive prompts the Copiloto may offer on this screen. */
  suggestions?: string[];
  /** Navigate-style actions shown alongside responses on this screen. */
  actions?: ChatScreenAction[];
};

const decodePathPart = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const tickerSuggestions = (ticker: string) => [
  `Compare ${ticker} com fundos similares`,
  `Quais são os principais indicadores de ${ticker}?`,
  `Resuma os últimos relatórios de ${ticker}`,
];

const tickerActions = (ticker: string) => [
  { label: `Proventos de ${ticker}`, href: ativoPath.tab(ticker, 'dividendos') },
  { label: `Relatórios de ${ticker}`, href: ativoPath.tab(ticker, 'relatorios') },
];

export function resolveChatScreenContext(pathname: string): ChatScreenContext {
  const portfolioMatch = pathname.match(/^\/carteira\/(\d+)(?:\/|$)/);
  if (portfolioMatch) {
    const base = `/carteira/${portfolioMatch[1]}`;
    return {
      id: 'portfolio',
      label: 'Esta carteira',
      description:
        'Os dados desta carteira estão disponíveis para perguntas sobre patrimônio, posições, dividendos e diversificação.',
      modelPrompt:
        'A tela atual é uma carteira do usuário. Considere esse contexto e use as ferramentas de carteira para consultar dados atuais antes de responder.',
      suggestions: [
        'Como está a diversificação desta carteira?',
        'Quais posições pagaram mais dividendos nos últimos 12 meses?',
        'Como está o patrimônio da carteira?',
      ],
      actions: [
        { label: 'Ver posições', href: `${base}/ativos` },
        { label: 'Ver dividendos', href: `${base}/dividendos` },
        { label: 'Ver diversificação', href: `${base}/patrimonio/diversificacao` },
      ],
    };
  }

  const tickerMatch = pathname.match(/^\/ativo\/([^/]+)(?:\/|$)/);
  if (tickerMatch) {
    const ticker = decodePathPart(tickerMatch[1]).toUpperCase();
    const segments = pathname.split('/').filter(Boolean);
    const screen = getCurrentFiiScreenDescriptor({
      ticker,
      tab: segments[2] ?? 'resumo',
      subview: segments[3],
    });
    if (screen) {
      return {
        id: screen.id,
        label: `${ticker} · ${screen.label}`,
        description: `Aba atual: ${screen.label}. Esta tela está disponível como contexto para perguntas sobre ${screen.purpose}.`,
        modelPrompt: `A tela atual é ${screen.title}. Esta aba mostra ${screen.purpose}. Se o usuário perguntar sobre esta aba ou seus dados, explique a estrutura ou consulte a ferramenta de dados correspondente; não invente valores.`,
        toolInput: screen.toolInput,
        suggestions: tickerSuggestions(ticker),
        actions: tickerActions(ticker),
      };
    }
    return {
      id: 'fund',
      label: ticker,
      description: `Esta página de ativo está disponível como contexto para perguntas sobre ${ticker}, seus indicadores, proventos e relatórios.`,
      modelPrompt: `A tela atual é a página do ativo ${ticker}. Considere esse ticker como o assunto inicial da conversa e consulte ferramentas para dados atuais.`,
      suggestions: tickerSuggestions(ticker),
      actions: tickerActions(ticker),
    };
  }

  if (pathname === '/mercado' || pathname.startsWith('/mercado/')) {
    return {
      id: 'market',
      label: 'Mercado',
      description:
        'Esta página está disponível como contexto para perguntas sobre IFIX, juros, inflação, câmbio e movimentos do mercado.',
      modelPrompt:
        'A tela atual é Mercado. Considere indicadores macroeconômicos e movimentos do mercado como contexto inicial e consulte as ferramentas de mercado para dados atuais.',
      suggestions: ['Como o mercado de FIIs está reagindo aos juros?', 'Qual o panorama do IFIX hoje?'],
      actions: [{ label: 'Ver rankings de FIIs', href: '/rankings-de-fiis' }],
    };
  }

  if (pathname === '/comparador' || pathname.startsWith('/comparador/')) {
    return {
      id: 'comparator',
      label: 'Comparador de fundos',
      description: 'Os fundos e indicadores desta área de comparação orientam as perguntas feitas ao Copiloto.',
      modelPrompt:
        'A tela atual é o Comparador de fundos. Considere que a conversa parte de uma comparação e use comparator_fundamentals quando houver tickers suficientes.',
    };
  }

  if (pathname === '/catalogo' || pathname.startsWith('/catalogo/')) {
    return {
      id: 'fund-catalog',
      label: 'Catálogo de fundos',
      description: 'Esta área está disponível como contexto para pesquisar, filtrar e comparar fundos.',
      modelPrompt:
        'A tela atual é o Catálogo de fundos. Considere descoberta e filtragem de fundos como contexto inicial e use screener_search para resultados atuais.',
    };
  }

  if (pathname === '/renda-fixa' || pathname.startsWith('/renda-fixa/')) {
    const view = pathname.startsWith('/renda-fixa/comparar')
      ? 'compare'
      : pathname.startsWith('/renda-fixa/simulador')
        ? 'simulate'
        : 'catalog';
    const screen = getCurrentFixedIncomeScreenDescriptor({ view });
    return {
      id: screen.id,
      label: screen.label,
      description: `Esta tela está disponível como contexto para perguntas sobre ${screen.purpose}.`,
      modelPrompt: `A tela atual é ${screen.title}. Considere títulos, indexadores e cenários de juros como contexto inicial e, se o usuário perguntar sobre a estrutura da tela, use a ferramenta de contexto correspondente.`,
      toolInput: screen.toolInput,
    };
  }

  if (pathname === '/painel' || pathname.startsWith('/painel/')) {
    return {
      id: 'dashboard',
      label: 'Seu painel',
      description: 'Esta visão consolidada está disponível como contexto para perguntas sobre seus investimentos.',
      modelPrompt: 'A tela atual é o painel consolidado do usuário. Considere essa visão geral como contexto inicial.',
    };
  }

  if (pathname === '/carteira') {
    return {
      id: 'portfolios',
      label: 'Suas carteiras',
      description: 'Esta área está disponível como contexto para perguntas sobre suas carteiras e sua organização.',
      modelPrompt: 'A tela atual é a lista de carteiras do usuário. Considere suas carteiras como contexto inicial.',
    };
  }

  if (pathname === '/' || pathname === '/landing/carteira') {
    return {
      id: pathname === '/' ? 'home' : 'portfolio-product',
      label: pathname === '/' ? 'Início' : 'Gerenciador de carteiras',
      description:
        pathname === '/'
          ? 'Esta visão inicial está disponível como contexto para explorar a plataforma e seus investimentos.'
          : 'Esta página está disponível como contexto para perguntas sobre organização de carteiras e patrimônio.',
      modelPrompt:
        pathname === '/'
          ? 'A tela atual é a página inicial da plataforma. Considere a exploração geral do produto como contexto inicial.'
          : 'A tela atual apresenta o gerenciador de carteiras. Considere organização de carteiras e patrimônio como contexto inicial.',
    };
  }

  if (pathname === '/ferramentas' || pathname.startsWith('/ferramentas/')) {
    return {
      id: pathname.includes('relatorios-ai') ? 'ai-reports-tools' : 'tools',
      label: pathname.includes('relatorios-ai') ? 'Relatórios IA' : 'Central de ferramentas',
      description: pathname.includes('relatorios-ai')
        ? 'Esta área está disponível como contexto para perguntas sobre briefings e relatórios com IA.'
        : 'Esta área está disponível como contexto para escolher e usar ferramentas de análise.',
      modelPrompt: pathname.includes('relatorios-ai')
        ? 'A tela atual é Relatórios IA. Considere briefings e relatórios automatizados como contexto inicial.'
        : 'A tela atual é a Central de ferramentas. Considere investigação de fundos, mercado e carteiras como contexto inicial.',
    };
  }

  if (pathname === '/relatorios-ia' || pathname.startsWith('/relatorios-ia/')) {
    return {
      id: 'ai-reports',
      label: 'Relatórios IA',
      description:
        'Esta área está disponível como contexto para perguntas sobre relatórios e briefings personalizados.',
      modelPrompt:
        'A tela atual é Relatórios IA. Considere relatórios e briefings personalizados como contexto inicial.',
    };
  }

  if (pathname === '/rankings-de-fiis' || pathname.startsWith('/rankings-de-fiis/')) {
    return {
      id: 'fund-rankings',
      label: 'Rankings de FIIs',
      description: 'Os rankings e indicadores desta área são o contexto inicial para perguntas sobre FIIs.',
      modelPrompt:
        'A tela atual é Rankings de FIIs. Considere os rankings como ponto de partida e consulte ferramentas para dados atuais.',
    };
  }

  if (pathname === '/ideias-para-investir-por-objetivo') {
    return {
      id: 'investment-ideas',
      label: 'Ideias por objetivo',
      description: 'Esta área está disponível como contexto para perguntas sobre objetivos e critérios de pesquisa.',
      modelPrompt:
        'A tela atual é Ideias por objetivo. Considere o objetivo selecionado pelo usuário como contexto inicial.',
    };
  }

  if (pathname === '/referencia-rapida' || pathname.startsWith('/referencia-rapida/')) {
    return {
      id: 'quick-reference',
      label: 'Referência rápida',
      description: 'Os conceitos e indicadores desta área estão disponíveis como contexto para suas perguntas.',
      modelPrompt:
        'A tela atual é Referência rápida. Considere o conceito ou indicador em estudo como contexto inicial.',
    };
  }

  if (pathname === '/blog' || pathname.startsWith('/blog/')) {
    return {
      id: 'blog',
      label: pathname === '/blog' ? 'Blog' : 'Artigo do blog',
      description: 'O conteúdo que você está lendo está disponível como contexto para perguntas e explicações.',
      modelPrompt:
        'A tela atual é o Blog. Considere o conteúdo visível como contexto de leitura, sem presumir fatos além dele.',
    };
  }

  if (pathname === '/cursos' || pathname.startsWith('/cursos/')) {
    return {
      id: 'courses',
      label: pathname === '/cursos' ? 'Cursos' : 'Aula do curso',
      description: 'O conteúdo educacional desta área está disponível como contexto para perguntas e explicações.',
      modelPrompt:
        'A tela atual é a área de Cursos. Considere a aula ou trilha visível como contexto educacional inicial.',
      suggestions: ['O que os cursos cobrem sobre diversificação?', 'Que cursos existem na plataforma?'],
      actions: [{ label: 'Ver catálogo de cursos', href: '/cursos' }],
    };
  }

  if (pathname === '/configuracoes' || pathname.startsWith('/configuracoes/')) {
    return {
      id: 'settings',
      label: 'Configurações',
      description: 'Esta área está disponível como contexto para perguntas sobre sua conta, preferências e acesso.',
      modelPrompt: 'A tela atual é Configurações. Considere conta, preferências e acesso como contexto inicial.',
    };
  }

  if (pathname.startsWith('/ajuda/')) {
    return {
      id: 'help',
      label: 'Ajuda',
      description: 'O guia de ajuda que você está lendo está disponível como contexto para suas perguntas.',
      modelPrompt:
        'A tela atual é uma página de Ajuda. Considere o procedimento visível como contexto educacional inicial.',
    };
  }

  if (pathname === '/contato' || pathname === '/sobre' || pathname === '/funcionalidades' || pathname === '/precos') {
    const labels: Record<string, string> = {
      '/contato': 'Contato e suporte',
      '/sobre': 'Sobre o Análise de FIIs',
      '/funcionalidades': 'Funcionalidades',
      '/precos': 'Planos e preços',
    };
    const label = labels[pathname];
    return {
      id: pathname.slice(1),
      label,
      description: `Esta página de ${label.toLocaleLowerCase('pt-BR')} está disponível como contexto para suas perguntas.`,
      modelPrompt: `A tela atual é ${label}. Considere o conteúdo visível como contexto inicial, sem inventar informações ausentes.`,
    };
  }

  if (pathname === '/chat' || pathname.startsWith('/chat/')) {
    return {
      id: 'general',
      label: 'Contexto geral',
      description:
        'Nesta área geral, o Copiloto consulta fundos, mercado, relatórios e suas carteiras quando você pedir.',
      modelPrompt: 'A conversa foi iniciada na área geral do Copiloto, sem uma tela de domínio específica.',
    };
  }

  return {
    id: 'current-page',
    label: 'Esta página',
    description: 'A área que você está visualizando está disponível como ponto de partida para suas perguntas.',
    modelPrompt: `A conversa foi aberta a partir da área ${pathname}. Use apenas essa identidade de navegação como contexto; não a trate como autorização ou como dados já consultados.`,
  };
}
